import React from "react";
import { Button, Column, Heading, isAnniversaryOnDate, Modal, Row, stringToLocalDate } from "@hex-insights/core";
import { BirthdayModal } from "../../../../../Components/BirthdayModal";
import { Icon } from "../../../../../Components/Icon";
import { Nav } from "../../../../../Components/Nav";
import { StudentDetailQuery } from "../../../../../Utilities";
import { PersonFormat, StudentFormat } from "../../../../Format";
import { FinancialsViewPermissionRequired } from "../../../_General";
import { StudentNoteCreateModalController } from "../../../StudentNote";
import { StudentEnrollmentStatus } from "../../Enrollment";
import { StudentIconWithFullModal } from "../../Icon";
import { DetailRouteContext } from "../RouteContext";
import { StudentAttendanceStatus } from "./Attendance";

export type HeaderProps = {
	student: StudentDetailQuery["student"];
};

export function Header({ student }: HeaderProps) {
	const { subPageInfos } = React.useContext(DetailRouteContext);

	const studentNoteFormStateArgs = React.useMemo(
		() => ({
			initialFormValues: {
				primaryStudentIDs: [student.id],
			},
		}),
		[student.id],
	);

	return (
		<Row justify="spaced-start" align="center">
			<StudentIconWithFullModal student={student} size={86} />

			<Row justify="space-between" align="center" style={{ flexGrow: 1 }}>
				<Column justify="spaced-start" verticalSpacing="1.25rem">
					<Row justify="spaced-start" align="center" horizontalSpacing="1.625rem" style={{ paddingLeft: "1.75rem" }}>
						<Heading.H1 noMargin>{StudentFormat.name(student)}</Heading.H1>

						<Row justify="spaced-start" align="center" horizontalSpacing="1.125rem">
							<StudentEnrollmentStatus student={student} />
							<StudentAttendanceStatus student={student} />
							<BirthdayStatus student={student} />
						</Row>
					</Row>

					<Nav horizontalSpacing="2.25rem">
						<Nav.Link to={subPageInfos.overview.to(student.id)} exact={subPageInfos.overview.exact}>
							Overview
						</Nav.Link>
						<Nav.Link to={subPageInfos.academics.to(student.id)} exact={subPageInfos.academics.exact}>
							Academics
						</Nav.Link>
						<Nav.Link to={subPageInfos.familyAndContact.to(student.id)} exact={subPageInfos.familyAndContact.exact}>
							Family &amp; Contact
						</Nav.Link>
						<Nav.Link to={subPageInfos.health.to(student.id)}>Health</Nav.Link>
						{subPageInfos.financials && (
							<FinancialsViewPermissionRequired>
								<Nav.Link to={subPageInfos.financials.to(student.id)}>Financials</Nav.Link>
							</FinancialsViewPermissionRequired>
						)}
						<Nav.Link to={subPageInfos.resources.to(student.id)}>Resources</Nav.Link>
					</Nav>
				</Column>

				<Column justify="spaced-start" align="flex-end" verticalSpacing="1rem">
					<Row justify="spaced-start">
						<StudentNoteCreateModalController
							variant="primary"
							formStateArgs={studentNoteFormStateArgs}
							formNameSuffix={student.id}
							style={{
								fontSize: "0.875rem",
								backgroundColor: "var(--theme-colors__green)",
								borderColor: "var(--theme-colors__green)",
							}}
						/>
						<Button
							variant="primary"
							onClick={() => {}}
							style={{
								fontSize: "0.875rem",
								backgroundColor: "var(--theme-colors__green)",
								borderColor: "var(--theme-colors__green)",
							}}
						>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
								<Icon.Upload size={14} /> Upload Document
							</Row>
						</Button>
					</Row>
				</Column>
			</Row>
		</Row>
	);
}

type BirthdayStatusProps = {
	student: StudentDetailQuery["student"];
};

function BirthdayStatus({ student }: BirthdayStatusProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const dateOfBirth = student.person.dateOfBirth ? stringToLocalDate(student.person.dateOfBirth, "day") : null;

	if (dateOfBirth === null || !isAnniversaryOnDate(dateOfBirth, new Date())) {
		return null;
	}

	return (
		<React.Fragment>
			<Button onClick={openModal} style={{ padding: 0, border: "none" }}>
				<Row
					justify="spaced-start"
					align="center"
					horizontalSpacing="0.375rem"
					style={{ color: "var(--theme-colors__orange)", fontWeight: "bold" }}
				>
					<Icon.Gift size={16} /> Happy Birthday!
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<BirthdayModal
					name={StudentFormat.name(student)}
					shortName={PersonFormat.Fields.givenName(student.person.givenName)}
					dateOfBirth={dateOfBirth}
					showAge
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}
